import React, { useEffect, useState, useRef } from "react";
import { useFormik } from "formik";
import { Helmet } from "react-helmet";
import * as Yup from "yup";
import { httpRequest } from "../utils/httpRequest";
import { toast } from "react-toastify";
import LanguageSwitcher from "../components/common/LanguageSwitcher";
import { useTranslation } from "react-i18next"; // ← اضافه شده

// بالا، بعد از importها اضافه کن:
function decodeJwtPayload(token) {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join(""),
    );
    return JSON.parse(jsonPayload);
  } catch {
    return {};
  }
}

// اگر بک‌اند perms/staff را داخل user برمی‌گرداند، همان را استفاده می‌کنیم؛
// وگرنه از payload توکن استخراج می‌کنیم.
function extractSession(user) {
  if (!user) return { perms: [], staff: false };
  let perms = user.perms || [];
  let staff = typeof user.staff === "boolean" ? user.staff : false;

  if (user.token && (!perms.length || !staff)) {
    const p = decodeJwtPayload(user.token);
    if (Array.isArray(p.perms)) perms = p.perms;
    if (typeof p.staff === "boolean") staff = p.staff;
  }

  return { ...user, perms, staff };
}

function hasOrgAccess(session) {
  return (session.perms || []).some(
    (k) => typeof k === "string" && k.startsWith("org:"),
  );
}

function isPlatformAdmin(session) {
  return (session.perms || []).some(
    (k) =>
      typeof k === "string" &&
      (k.startsWith("platform:") || k.startsWith("admin:")),
  );
}

export const redirectToDashboard = (userObj) => {
  console.log("🔍 userObj:", userObj); // 👈 لاگ ۱
  const session = extractSession(
    userObj || JSON.parse(localStorage.getItem("user")),
  );
  console.log("🧠 session after extract:", session); // 👈 لاگ ۲
  console.log("📜 perms:", session.perms); // 👈 لاگ ۳

  if (isPlatformAdmin(session)) {
    console.log("➡️ redirect: /organization");
    window.location.href = "/organization";
    return;
  }
  if (hasOrgAccess(session)) {
    console.log("➡️ redirect: /organization");
    window.location.href = "/organization";
    return;
  }

  console.log("➡️ redirect: /dashboard");
  window.location.href = "/dashboard";
};

const LoginPage = () => {
  const { t } = useTranslation(); // ← اضافه شده
  const [step, setStep] = useState(0); // 0: input, 1: code, 2: complete-info
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState("");
  const [method, setMethod] = useState("phone"); // 'phone' or 'email'
  const codeInputsRef = useRef([]);

  const years = Array.from({ length: 100 }, (_, i) => 1403 - i);
  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  const days = Array.from({ length: 31 }, (_, i) => i + 1);

  const loginFormik = useFormik({
    initialValues: { phone: "", email: "" },
    validationSchema: Yup.object({
      phone: Yup.string().when("method", {
        is: "phone",
        then: Yup.string()
          .matches(/^[0-9]{10}$/, t("validation.phone_format"))
          .required(t("validation.phone_required")),
      }),
      email: Yup.string().when("method", {
        is: "email",
        then: Yup.string()
          .email(t("validation.email_format"))
          .required(t("validation.email_required")),
      }),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const payload =
          method === "phone"
            ? { phone: `0${values.phone}` }
            : { email: values.email };
        method === "phone"
          ? await httpRequest("POST", "/user/sign", payload)
          : await httpRequest("POST", "/user/sign/email", payload);

        toast.success(t("toast.code_sent"));
        setStep(1);
      } catch (err) {
        toast.error(err?.response?.data?.message || t("toast.code_send_error"));
        loginFormik.resetForm();
      } finally {
        setLoading(false);
      }
    },
  });

  const approveFormik = useFormik({
    initialValues: { code1: "", code2: "", code3: "", code4: "" },
    validationSchema: Yup.object({
      code1: Yup.string()
        .matches(/^[0-9]$/, "")
        .required(),
      code2: Yup.string()
        .matches(/^[0-9]$/, "")
        .required(),
      code3: Yup.string()
        .matches(/^[0-9]$/, "")
        .required(),
      code4: Yup.string()
        .matches(/^[0-9]$/, "")
        .required(),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      const code = `${values.code1}${values.code2}${values.code3}${values.code4}`;
      try {
        const payload =
          method === "phone"
            ? { code, phone: `0${loginFormik.values.phone}` }
            : { code, email: loginFormik.values.email };

        const { data } = await httpRequest("POST", "/user/accept", payload, {});
        const user = data?.data;
        localStorage.setItem("user", JSON.stringify(user));
        toast.success(t("toast.login_success"));

        if (!user?.name) {
          setToken(user.token);
          setStep(2);
        } else {
          redirectToDashboard(user);
        }
      } catch (err) {
        toast.error(err?.response?.data?.message || t("toast.code_wrong"));
        approveFormik.resetForm();
      } finally {
        setLoading(false);
      }
    },
  });

  const completeInfoFormik = useFormik({
    initialValues: { name: "", year: "", month: "", day: "" },
    validationSchema: Yup.object({
      name: Yup.string().required(t("validation.name_required")),
      year: Yup.string().required(t("validation.birthdate_required")),
      month: Yup.string().required(t("validation.birthdate_required")),
      day: Yup.string().required(t("validation.birthdate_required")),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const birthdate = `${values.year}-${values.month.padStart(
          2,
          "0",
        )}-${values.day.padStart(2, "0")}`;
        const { data } = await httpRequest(
          "POST",
          "/user/complete-info",
          { name: values.name, birthdate },
          { "x-access-token": token },
        );
        const user = data?.data;
        localStorage.setItem("user", JSON.stringify(user));
        toast.success(t("toast.info_saved"));
        window.location.href = "/dashboard";
      } catch (err) {
        toast.error(t("toast.info_save_error"));
      } finally {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user?.token && user?.name) redirectToDashboard(user);
  }, []);

  const handleCodeInputChange = (e, index) => {
    const { value } = e.target;
    if (/^[0-9]$/.test(value) || value === "") {
      approveFormik.setFieldValue(`code${index + 1}`, value);
      if (value !== "" && index < 3) {
        codeInputsRef.current[index + 1].focus();
      }
    }
  };

  const handleCodeInputKeyDown = (e, index) => {
    if (
      e.key === "Backspace" &&
      index > 0 &&
      approveFormik.values[`code${index + 1}`] === ""
    ) {
      codeInputsRef.current[index - 1].focus();
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
      <Helmet>
        <title>{t("login.page_title")}</title>
      </Helmet>
      <div className="bg-white shadow-xl rounded-2xl px-6 py-8 w-full max-w-sm">
        <div className="mb-4 flex justify-start">
          <LanguageSwitcher />
        </div>
        <div className="text-center mb-6">
          <h2 className="text-2xl font-extrabold text-gray-800">
            {t("login.title")}
          </h2>
          <p className="text-gray-500 text-sm mt-1">
            {t("login.welcome_message")}
          </p>
        </div>

        {step === 0 && (
          <form onSubmit={loginFormik.handleSubmit} className="space-y-6 ">
            <div className="flex justify-center mb-4 gap-4">
              <button
                type="button"
                onClick={() => setMethod("phone")}
                className={`px-4 py-2 rounded-md border ${
                  method === "phone"
                    ? "bg-purple-600 text-white"
                    : "bg-white text-gray-600"
                }`}
              >
                {t("login.login_with_phone")}
              </button>
              <button
                type="button"
                onClick={() => setMethod("email")}
                className={`px-4 py-2 rounded-md border ${
                  method === "email"
                    ? "bg-purple-600 text-white"
                    : "bg-white text-gray-600"
                }`}
              >
                {t("login.login_with_email")}
              </button>
            </div>

            {method === "phone" ? (
              <div>
                <label
                  htmlFor="phone"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {t("login.phone_number")}
                </label>
                <div className="flex items-center gap-2" dir="ltr">
                  <span className="text-gray-700 font-semibold border border-gray-300 rounded-md px-3 py-2 bg-gray-50">
                    +98
                  </span>
                  <input
                    id="phone"
                    name="phone"
                    maxLength={10}
                    inputMode="numeric"
                    placeholder="9123456789"
                    value={loginFormik.values.phone}
                    onChange={loginFormik.handleChange}
                    className="w-full border rounded-md px-4 py-2 text-base focus:ring-2 focus:ring-purple-400"
                  />
                </div>
                {loginFormik.errors.phone && (
                  <p className="text-red-600 text-xs mt-2">
                    {loginFormik.errors.phone}
                  </p>
                )}
              </div>
            ) : (
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {t("login.email")}
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={loginFormik.values.email}
                  onChange={loginFormik.handleChange}
                  className="w-full border rounded-md px-4 py-2 text-base focus:ring-2 focus:ring-purple-400"
                />
                {loginFormik.errors.email && (
                  <p className="text-red-600 text-xs mt-2">
                    {loginFormik.errors.email}
                  </p>
                )}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className={`w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white font-bold py-3 rounded-lg hover:from-purple-700 hover:to-blue-700 transition duration-300 ${
                loading ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {loading ? t("loading.sending") : t("login.send_code")}
            </button>
          </form>
        )}

        {step === 1 && (
          <form onSubmit={approveFormik.handleSubmit} className="space-y-6 ">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t("login.verification_code")}
            </label>
            <p className="text-gray-500 text-sm mb-4">
              {t("login.code_sent_to", {
                target:
                  method === "phone"
                    ? `0${loginFormik.values.phone}`
                    : loginFormik.values.email,
              })}
            </p>
            <div dir="ltr" className="flex justify-center gap-3">
              {[1, 2, 3, 4].map((_, index) => (
                <input
                  key={index}
                  maxLength={1}
                  type="text"
                  name={`code${index + 1}`}
                  value={approveFormik.values[`code${index + 1}`]}
                  onChange={(e) => handleCodeInputChange(e, index)}
                  onKeyDown={(e) => handleCodeInputKeyDown(e, index)}
                  ref={(el) => (codeInputsRef.current[index] = el)}
                  className="w-12 h-12 text-center text-xl font-bold border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400"
                />
              ))}
            </div>
            <button
              type="submit"
              disabled={loading}
              className={`w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white font-bold py-3 rounded-lg hover:from-purple-700 hover:to-blue-700 transition duration-300 ${
                loading ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {loading ? t("loading.verifying") : t("login.verify_code")}
            </button>
            <button
              type="button"
              onClick={() => setStep(0)}
              className="w-full text-center text-sm text-blue-600 hover:text-blue-800 mt-4"
            >
              {t("login.back")}
            </button>
          </form>
        )}

        {step === 2 && (
          <form
            onSubmit={completeInfoFormik.handleSubmit}
            className="space-y-6"
          >
            <div>
              <label
                htmlFor="name"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                {t("login.name")}
              </label>
              <input
                id="name"
                name="name"
                value={completeInfoFormik.values.name}
                onChange={completeInfoFormik.handleChange}
                className="w-full border border-gray-300 rounded-md px-4 py-2 text-base focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-purple-400 transition duration-200"
              />
              {completeInfoFormik.errors.name &&
                completeInfoFormik.touched.name && (
                  <p className="text-red-600 text-xs mt-2">
                    {completeInfoFormik.errors.name}
                  </p>
                )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("login.birthdate")}
              </label>
              <div className="flex gap-3">
                <select
                  name="year"
                  value={completeInfoFormik.values.year}
                  onChange={completeInfoFormik.handleChange}
                  className="w-1/3 border border-gray-300 rounded-md px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-purple-400 transition duration-200"
                >
                  <option value="">{t("login.year")}</option>
                  {years.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
                <select
                  name="month"
                  value={completeInfoFormik.values.month}
                  onChange={completeInfoFormik.handleChange}
                  className="w-1/3 border border-gray-300 rounded-md px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-purple-400 transition duration-200"
                >
                  <option value="">{t("login.month")}</option>
                  {months.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
                <select
                  name="day"
                  value={completeInfoFormik.values.day}
                  onChange={completeInfoFormik.handleChange}
                  className="w-1/3 border border-gray-300 rounded-md px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-purple-400 transition duration-200"
                >
                  <option value="">{t("login.day")}</option>
                  {days.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
              </div>
              {(completeInfoFormik.errors.year ||
                completeInfoFormik.errors.month ||
                completeInfoFormik.errors.day) &&
                (completeInfoFormik.touched.year ||
                  completeInfoFormik.touched.month ||
                  completeInfoFormik.touched.day) && (
                  <p className="text-red-600 text-xs mt-2">
                    {t("validation.birthdate_complete_required")}
                  </p>
                )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white font-bold py-3 rounded-lg shadow-md hover:from-purple-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition duration-300 ${
                loading ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {loading ? t("loading.sending") : t("login.save_info")}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default LoginPage;
